// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngZone from "@adl/App/Components/Common/XngZone";
import {
    CopyRightWrapper,
    FooterBottomSection,
    FooterContainerWrapper,
    FooterContentWrapper,
    FooterDivider,
    FooterLeftSection,
    FooterRightSection,
    FooterSiteMapLinksWrapper,
    FooterTopSection,
    GeneralLinksWrapper,
} from "@adl/Overrides/Widgets/Footer/FooterContainer.style";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {
    extendedStyles?: FooterContainerStyles;
}

export interface FooterContainerStyles {
    widgetGridContainer?: GridContainerProps;
    logoGridItem?: GridItemProps;
    linkList1GridItem?: GridItemProps;
    linkList2GridItem?: GridItemProps;
    linkList3GridItem?: GridItemProps;
    linkList4GridItem?: GridItemProps;
    subscribeGridItem?: GridItemProps;
    socialLinksGridItem?: GridItemProps;
    siteMapLinksGridItem?: GridItemProps;
    richContentGridItem?: GridItemProps;
    logoWrapper?: InjectableCss;
}

export const footerContainerStyles: FooterContainerStyles = {
    logoGridItem: {
        width: 12,
        css: css`
            width: 100%;
            display: flex;
            justify-content: center;
            img {
                height: 78px;
            }
        `,
    },
    linkList1GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    linkList2GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    linkList3GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    linkList4GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    subscribeGridItem: {
        width: [12, 12, 8, 4, 4],
    },
    socialLinksGridItem: {
        width: 12,
    },
    siteMapLinksGridItem: {
        width: 12,
    },
    richContentGridItem: {
        width: 12,
    },
    logoWrapper: {
        css: css`
            width: 110px;
        `,
    },
};

const FooterContainer: FC<OwnProps> = ({ id, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(footerContainerStyles, extendedStyles));
    const copyrightMessage = () => {
        const message = siteMessage("Custom_Footer_Copyright") as string;
        return message.replace("{0}", String(new Date().getFullYear()));
    };
    return (
        <FooterContainerWrapper>
            <FooterContentWrapper>
                <FooterTopSection>
                    <FooterLeftSection>
                        <StyledWrapper>
                            <XngZone hint="Zone for Logo" zoneName="Logo" contentId={id} />
                            <Hidden above="xs">
                                <XngZone hint="Zone for Social Links" zoneName="SocialLinks" contentId={id} />
                            </Hidden>
                        </StyledWrapper>
                        <StyledWrapper className="footer-address-wrapper">
                            <XngZone hint="Zone for Address" zoneName="FooterAddress" contentId={id} />
                            <XngZone hint="Zone for Phone And Hours" zoneName="FooterPhone" contentId={id} />
                            <XngZone
                                hint="Zone for Email"
                                zoneName="FooterEmail"
                                contentId={id}
                                data-test-selector="email-zone"
                            />
                        </StyledWrapper>
                        <Hidden below="sm" className="footer-social-links-wrapper-desktop">
                            <XngZone hint="Zone for Social Links" zoneName="SocialLinks" contentId={id} />
                        </Hidden>
                    </FooterLeftSection>
                    <FooterRightSection>
                        <GeneralLinksWrapper>
                            <Zone zoneName="LinkList1" contentId={id} />
                        </GeneralLinksWrapper>
                        <GeneralLinksWrapper>
                            <Zone zoneName="LinkList2" contentId={id} />
                        </GeneralLinksWrapper>
                        <GeneralLinksWrapper>
                            <Zone zoneName="LinkList3" contentId={id} />
                        </GeneralLinksWrapper>
                        <GeneralLinksWrapper>
                            <Zone zoneName="LinkList4" contentId={id} />
                        </GeneralLinksWrapper>
                    </FooterRightSection>
                </FooterTopSection>
            </FooterContentWrapper>
            <FooterDivider />
            <FooterContentWrapper>
                <FooterBottomSection>
                    <CopyRightWrapper>{copyrightMessage()}</CopyRightWrapper>
                    <FooterSiteMapLinksWrapper>
                        <Zone zoneName="RichContent" contentId={id} />
                    </FooterSiteMapLinksWrapper>
                </FooterBottomSection>
            </FooterContentWrapper>
        </FooterContainerWrapper>
    );
};

const footerContainer: WidgetModule = {
    component: FooterContainer,
    definition: {
        group: "Footer",
        allowedContexts: ["Footer"],
    },
};

export default footerContainer;
